import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import {
  ActionButton,
  FileTrigger,
  Flex,
  Text,
  TextField,
  View,
  Content,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import { ToastQueue } from '@adobe/react-spectrum';
import {
  AttachmentIcon,
  CancelIcon,
  MoreIcon,
  NewMessageIcon,
  TemplateIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { WindowActionsBar } from "@progress/kendo-react-dialogs";
import { RadioButton } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import ContactsContactMultipleTag from "../../../../../components/protected/layout-components/FilterComponent/ContactsContactMultipleTagComponent/ContactsContactMultipleTag";
import ContactsFilterMultipleTag from "../../../../../components/protected/layout-components/FilterComponent/ContactsFilterMultipleTagComponent/ContactsFilterMultipleTag";
import WindowComponent from "../../../../../components/ui/WindowComponent";
import { invokeNewMessageCreated } from "../../../../../utils/NavigationUtils";
import { useViewInfo } from "../../../../../hooks/UseViewInfo";
import { useDeviceDetection } from "../../../../../hooks/UseDeviceDetection";
import TinyMCEEditor from "../../../../../components/ui/TinyMCEEditor/TinyMCEEditior";
import {
  ContextMenuComponent,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { useLocation } from "react-router-dom";
import { SessionInfo } from "../../../../../data/SessionInfo";
import {
  addKey,
  useKeyboardShortcuts,
} from "../../../../../hooks/UseKeyboardShortcuts";
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { ContextSelection } from "../../../../../components/protected/Search/ContextSelection";
import type {
  ContextObject,
  ContextTypeKind,
} from "../../../../../components/protected/Search/ContextSelection/types";
import styles from "./new_message_component.module.css";
import { normalizeAtollonNumericId } from "../../../../../utils/helperUtil";
import {
  formStateHasNotifyUsers,
  getComposeSendPreflightFailure,
} from "../../../../../utils/composeSendPreflight";

// Import shared message hooks and utilities
import {
  useNewMessageForm,
  useNewMessageSettings,
  useNewMessageFiles,
  sendMessage,
  saveDraftMessage,
  truncateText,
  type MessageOperationsConfig,
} from "../../../../../hooks/message";

export interface INewMessageDialogProps {
  windowIndex: number;
  selectedItemId: any;
  /** When set (e.g. Communication app folder / Team Inbox), used as explicit context id for compose. */
  composeFolderContextId?: number;
  /** Context id from the source message, used to preload context for reply/forward flows. */
  defaultContextId?: string | number;
  /** Context type for defaultContextId; keeps send binding aligned while ContextSelection resolves. */
  defaultContextType?: ContextTypeKind;
  /** User default inbox node from GetUser.messageNode; used to detect "no extra context" vs folder id. */
  defaultInboxMessageNodeId?: number;
  replyType?: string;
  messageObj?: any;
  emailAttachments?: any;
  isOpen: boolean;
  messageType?: string;
  forcedMessageType?: string;
  closeMessageDialog: (reload: boolean) => void;
}

function NewMessageDialog(props: Readonly<INewMessageDialogProps>) {
  const { state } = useLocation();
  const session: SessionInfo = state.session;
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const isPortraitTablet =
    (deviceInfo.isTablet || deviceInfo.isIPad) &&
    deviceInfo.orientation !== "landscape";
  const isTruePhoneLayout =
    isMobile && !deviceInfo.isTablet && !deviceInfo.isIPad;
  const isCompactSheetLayout = isTruePhoneLayout || isPortraitTablet;
  const {
    windowIndex,
    selectedItemId,
    composeFolderContextId,
    defaultContextId,
    defaultInboxMessageNodeId,
    isOpen,
    closeMessageDialog,
    replyType,
    messageObj,
    emailAttachments,
    forcedMessageType,
    // messageType is passed but not used in this component
  } = props;
  const { store, messageService, mailService } = useDependency();
  const { t } = useTranslation();

  // Use shared hooks
  const {
    formData: newEmailFields,
    formStateRef,
    setFormData: setNewEmailFields,
    createParams,
  } = useNewMessageForm();

  const [fromEmailAddress, setFromEmailAddress] = useState<any>([]);
  const [messageTypeOptions, setMessageTypeOptions] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showCcNotify, setShowCcNotify] = useState<boolean>(false);

  const txtSubjectRef = useRef<any>(null);
  const contextMenuRef = useRef<ContextMenuComponent | null>(null);

  // Use shared settings hook
  const {
    isLoading: settingsLoading,
    mailboxSignature,
    changeMailbox,
    populateReplyTemplate,
  } = useNewMessageSettings({
    session,
    messageObj,
    replyType,
    onSettingsLoaded: (settings, defaultFields) => {
      const forcedMessageTypeOption = forcedMessageType
        ? settings.messageTypes.find(
            (item: any) => String(item.type) === String(forcedMessageType),
          )
        : null;
      setFromEmailAddress(settings.mailboxes);
      setMessageTypeOptions(settings.messageTypes);
      setNewEmailFields({
        ...defaultFields,
        from: defaultFields.from,
        fromValue: defaultFields.fromValue,
        messageType: forcedMessageTypeOption?.type || defaultFields.messageType,
        messageTypeValue:
          forcedMessageTypeOption || defaultFields.messageTypeValue,
        mailBox: settings.mailboxSignature,
        emailContent: settings.mailboxSignature,
      });
    },
    onError: (error) => {
      console.error("Failed to load communication settings", { error });
    },
  });

  const {
    folderStatusMapping,
    projectStatusMapping,
    activityStatusMapping,
  } = usePreloadAssets();

  const [messageContextSelection, setMessageContextSelection] =
    useState<ContextObject | null>(null);

  const contextSelectionDefaultValue = useMemo(() => {
    const sourceContextId = Number(defaultContextId);
    if (
      defaultContextId != null &&
      Number.isFinite(sourceContextId) &&
      sourceContextId > 0
    ) {
      return sourceContextId;
    }

    const folderId = composeFolderContextId;
    if (folderId != null && Number.isFinite(folderId) && folderId > 0) {
      return folderId;
    }
    const n = Number(selectedItemId);
    const defaultMessageNodeId = Number(
      defaultInboxMessageNodeId ?? session?.sessionInfo?.msgNode,
    );
    if (!Number.isFinite(n) || n <= 0) return null;
    return n === defaultMessageNodeId ? null : n;
  }, [
    composeFolderContextId,
    defaultContextId,
    selectedItemId,
    defaultInboxMessageNodeId,
    session?.sessionInfo?.msgNode,
  ]);

  useEffect(() => {
    setMessageContextSelection(null);
  }, [selectedItemId, composeFolderContextId, defaultContextId]);

  const onContextPathResolveError = useCallback((error: unknown) => {
    console.error("ContextSelection default context resolve failed", error);
  }, []);

  const resolvedContextId =
    messageContextSelection?.contextId ?? contextSelectionDefaultValue ?? "";

  const shouldReloadCommunicationList = useMemo(() => {
    const selectedContext = normalizeAtollonNumericId(resolvedContextId);
    const defaultInboxNode = normalizeAtollonNumericId(
      defaultInboxMessageNodeId ?? session?.sessionInfo?.msgNode,
    );

    if (selectedContext == null) {
      return true;
    }

    if (defaultInboxNode == null) {
      return false;
    }

    return selectedContext === defaultInboxNode;
  }, [
    resolvedContextId,
    defaultInboxMessageNodeId,
    session?.sessionInfo?.msgNode,
  ]);

  /** Default folder-backed message list context when selection is cleared. */
  const defaultFolderContext = true;

  const hasSelectedContext =
    messageContextSelection !== null || contextSelectionDefaultValue != null;

  const resolvedIsFolderContext =
    hasSelectedContext ? false : defaultFolderContext;

  const displayContextSelection = useMemo(() => {
    if (!messageContextSelection) return null;

    return {
      ...messageContextSelection,
      pathSegmentContexts: messageContextSelection.pathSegmentContexts?.map(
        (segment) => ({
          id: segment.id,
          type: segment.type,
        }),
      ),
    };
  }, [messageContextSelection]);

  const requiresExplicitContextSelection = useMemo(() => {
    // Spec: when commenting/resolving an original outside context, UI must force a context pick.
    // We treat forced internal message ("1") as the comment/resolve compose flow.
    const isCommentOrResolveFlow = String(forcedMessageType ?? "") === "1";
    if (!isCommentOrResolveFlow) return false;

    const hasContext =
      Boolean(
        messageObj?.folderId ||
          messageObj?.projectId ||
          messageObj?.activityRefId ||
          messageObj?.folderRefId ||
          messageObj?.projectRefId,
      ) ||
      Boolean(
        messageObj?.MESSAGE?.folderId ||
          messageObj?.MESSAGE?.projectId ||
          messageObj?.MESSAGE?.activityRefId ||
          messageObj?.MESSAGE?.folderRefId ||
          messageObj?.MESSAGE?.projectRefId,
      );

    // If the target already has context, comment is allowed without extra selection.
    return !hasContext;
  }, [forcedMessageType, messageObj]);

  // Use shared file management hook
  const {
    fileList,
    handleFilesAsync,
    deleteFile,
    uploadFiles,
    getRootProps,
    getInputProps,
    isDragActive,
  } = useNewMessageFiles({
    contextId: resolvedContextId,
    fallbackTreeHandle: resolvedIsFolderContext
      ? messageObj?.treeHandle || session?.sessionInfo?.msgNode
      : undefined,
    initialAttachments: emailAttachments,
  });

  let menuItems: MenuItemModel[] = [
    {
      iconCss: "bi bi-file-plus-fill",
      text: t("Template", { ns: "layout_components" }),
      id: "template",
    },
    {
      iconCss: "bi bi-gear-fill",
      text: t("Advance", { ns: "layout_components" }),
      id: "advance",
    },
    {
      iconCss: "bi bi-pencil-square",
      text: t("save_draft_and_close", { ns: "layout_components" }),
      id: "draft",
    },
  ];

  // Show loader while settings are loading
  useEffect(() => {
    setShowLoader(settingsLoading);
  }, [settingsLoading]);

  // Handle reply template population
  useEffect(() => {
    if (replyType && messageObj) {
      const replyFields = populateReplyTemplate();
      if (replyFields) {
        setNewEmailFields((prev: any) => ({ ...prev, ...replyFields }));
        if (
          (replyFields.cc?.length ?? 0) > 0 ||
          (replyFields.notify?.length ?? 0) > 0
        ) {
          setShowCcNotify(true);
        }
      }
    }
  }, [
    mailboxSignature,
    replyType,
    messageObj,
    populateReplyTemplate,
    setNewEmailFields,
  ]);

  // Message operations config
  const getMessageOpsConfig = useCallback(
    (): MessageOperationsConfig => ({
      mailService,
      messageService,
      serverService: store.Server,
      sessionId: store.SessionId,
      contextId: resolvedContextId,
      messageTypes: messageTypeOptions,
      parentMessageId: messageObj?.id,
      messageGroupId: messageObj?.messageGroup,
      treeHandle: messageObj?.treeHandle,
      userMessageGroup: session?.sessionInfo?.msgGroup,
      userMessageTreeHandle: session?.sessionInfo?.msgNode,
      isFolderContext: resolvedIsFolderContext,
    }),
    [
      mailService,
      messageService,
      store.Server,
      store.SessionId,
      resolvedContextId,
      resolvedIsFolderContext,
      messageTypeOptions,
      messageObj,
      session?.sessionInfo?.msgGroup,
      session?.sessionInfo?.msgNode,
    ],
  );

  // Send email/message using shared utility
  const handleSendEmail = useCallback(async () => {
    const hasNotifyUsers = formStateHasNotifyUsers(formStateRef.current as any);
    const failure = getComposeSendPreflightFailure({
      hasNotifyUsers,
      resolvedIsFolderContext,
      messageType: (formStateRef.current as any)?.messageType,
      requiresExplicitContextSelection,
    });
    if (failure === "notify_requires_project_or_activity") {
      ToastQueue.negative(
        t("please_choose_context", {
          ns: "layout_components",
          defaultValue:
            "Please choose a context (project or activity) when using Notify.",
        }),
        { timeout: 3000 },
      );
      return;
    }
    if (failure === "explicit_context_before_send") {
      ToastQueue.negative(
        t("please_choose_context", {
          ns: "layout_components",
          defaultValue: "Please choose a context (project or activity) before sending.",
        }),
        { timeout: 3000 },
      );
      return;
    }

    try {
      setShowLoader(true);
      const documentIds = await uploadFiles();
      const result = await sendMessage(
        getMessageOpsConfig(),
        formStateRef.current,
        documentIds,
        formStateRef.current.emailContent,
      );
      if (result.success) {
        invokeNewMessageCreated(String(resolvedContextId), "message_created");
        closeMessageDialog(shouldReloadCommunicationList);
      }
    } finally {
      setShowLoader(false);
    }
  }, [
    getMessageOpsConfig,
    uploadFiles,
    closeMessageDialog,
    resolvedContextId,
    shouldReloadCommunicationList,
    formStateRef,
    requiresExplicitContextSelection,
    resolvedIsFolderContext,
    t,
  ]);

  // Save draft using shared utility
  const handleSaveDraft = useCallback(async () => {
    try {
      setShowLoader(true);
      const documentIds = await uploadFiles();
      const result = await saveDraftMessage(
        getMessageOpsConfig(),
        formStateRef.current,
        documentIds,
        formStateRef.current.emailContent,
      );
      if (result.success) {
        closeMessageDialog(false);
      }
    } finally {
      setShowLoader(false);
    }
  }, [getMessageOpsConfig, uploadFiles, closeMessageDialog, formStateRef]);

  // Context menu event listener setup
  useEffect(() => {
    const targetElement = document.querySelector(".context-target");
    if (targetElement) {
      targetElement.addEventListener("click", handleContextMenu);
    }
    return () => {
      if (targetElement) {
        targetElement.removeEventListener("click", handleContextMenu);
      }
    };
  }, [newEmailFields]);

  const handleContextMenu = (event: any) => {
    // Prevent the default context menu
    event.preventDefault();
    // Open the custom context menu at the click position
    if (contextMenuRef.current) {
      contextMenuRef.current.open(event.pageY, event.pageX);
    }
  };

  const handleMenuItemClick = useCallback(
    (args: any) => {
      switch (args?.item?.id) {
        case "template":
          console.log("Template clicked");
          break;
        case "advance":
          console.log("Advance clicked");
          break;
        case "draft":
          handleSaveDraft();
          break;
      }
    },
    [handleSaveDraft],
  );

  const handleClose = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      closeDialog(false);
    }
  };

  const closeDialog = (flag: boolean) => {
    closeMessageDialog(flag);
  };

  const focusSubjectField = useCallback(() => {
    window.setTimeout(() => {
      const subjectRef = txtSubjectRef.current as any;
      if (subjectRef?.focus) {
        subjectRef.focus();
        return;
      }

      const subjectInput = document.querySelector(
        "#txtSubject input, #txtSubject",
      ) as HTMLInputElement | null;
      subjectInput?.focus();
      subjectInput?.select?.();
    }, 60);
  }, []);

  useKeyboardShortcuts(
    [
      ...addKey("Enter", { ctrl: true }, () => {
        if (!showLoader) {
          handleSendEmail();
        }
      }),
      ...addKey("z", { ctrl: true, shift: true }, () => {
        if (!showLoader) {
          closeDialog(false);
        }
      }),
      ...addKey("Escape", { ctrl: false, shift: false, alt: false }, () => {
        if (!showLoader) {
          closeDialog(false);
        }
      }),
    ],
    isOpen,
  );

  useEffect(() => {
    if (isOpen && !showLoader) {
      focusSubjectField();
    }
  }, [focusSubjectField, isOpen, showLoader]);

  // Mailbox change handler using shared hook
  const handleMailboxChange = async (event: any) => {
    const fromEmailId = fromEmailAddress.find(
      (item: any) => item.name === event.value.name,
    )?.id;
    const newSignature = await changeMailbox(fromEmailId);
    setNewEmailFields((prev: any) => ({
      ...prev,
      from: event.value.name,
      fromValue: { name: event.value.name, id: event.value.id },
      mailBox: newSignature,
      emailContent: prev.emailContent.replace(prev.mailBox || "", newSignature),
    }));
  };

  function renderDesktopEmailOptionalFields(): React.ReactElement {
    return (
      <>
        <Flex
          direction={"row"}
          top={"5px"}
          alignSelf={"stretch"}
          position={"relative"}
          flexShrink={0}
          gap={"size-200"}
        >
          <View
            position={"relative"}
            width={"5%"}
            left={"8px"}
            top={"5px"}
          >
            {t("notify", { ns: "layout_components" })}:
          </View>
          <View
            width={"92%"}
            marginStart={"17px"}
            // UNSAFE_style={{ overflow: "auto" }}
            UNSAFE_className={styles.label_wrapper}
          >
            <ContactsFilterMultipleTag
              createParams={createParams}
              setContactsSelected={newEmailFields.notify}
              fieldName={"notify"}
              params={{
                onlyIds: false,
                showOnlyUc: false,
                onlyUser: 1,
                conjuctionDistrGroups: 0,
                ORDER: "",
              }}
            />
          </View>
        </Flex>
        {fromEmailAddress.length > 2 && (
          <Flex
            direction={"row"}
            top={"5px"}
            alignSelf={"stretch"}
            position={"relative"}
            flexShrink={0}
            gap={"size-200"}
          >
            <View
              position={"relative"}
              width={"5%"}
              left={"8px"}
              top={"5px"}
            >
              {t("from", { ns: "layout_components" })}:
            </View>
            <View
              width={"92%"}
              marginStart={"17px"}
              position={"relative"}
              UNSAFE_className={styles.label_wrapper}
            >
              <DropDownList
                value={newEmailFields.fromValue}
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  zIndex: 0,
                  border: "none",
                  marginLeft: "0",
                }}
                data={fromEmailAddress}
                textField="name"
                dataItemKey="id"
                onChange={(event: DropDownListChangeEvent) => {
                  handleMailboxChange(event);
                }}
              />
            </View>
          </Flex>
        )}
      </>
    );
  }

  function renderDesktopEmailPrimaryRow(): React.ReactElement {
    return (
      <Flex
        direction={"row"}
        top={"5px"}
        alignSelf={"stretch"}
        position={"relative"}
        flexShrink={0}
        gap={"size-200"}
      >
        <View
          position={"relative"}
          width={"5%"}
          left={"8px"}
          top={"5px"}
        >
          {t("to", { ns: "layout_components" })}:
        </View>
        <View
          width={"92%"}
          marginStart={"17px"}
          UNSAFE_style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            minWidth: 0,
          }}
        >
          <View
            width={"42%"}
            //UNSAFE_style={{ overflow: "auto" }}
            UNSAFE_className={styles.label_wrapper}
          >
            <ContactsContactMultipleTag
              createParams={createParams}
              fieldName={"to"}
              specificType="email"
              setContactsSelected={newEmailFields.to}
            />
          </View>

          {!showCcNotify ? (
            <View width={"58%"} UNSAFE_className={styles.label_wrapper}>
              <button
                type="button"
                className={styles.advanced_fields_trigger}
                onClick={() => setShowCcNotify(true)}
              >
                {fromEmailAddress.length > 2
                  ? t("cc_notify_from", {
                      ns: "layout_components",
                      defaultValue: "Cc, notify, from:",
                    })
                  : t("cc_notify", {
                      ns: "layout_components",
                      defaultValue: "Cc, notify:",
                    })}
              </button>
            </View>
          ) : (
            <View
              width={"58%"}
              UNSAFE_style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: 0,
              }}
            >
              <View
                position={"relative"}
                width={"10%"}
                top={"5px"}
                UNSAFE_style={{ minWidth: "24px" }}
              >
                {t("cc", { ns: "layout_components" })}:
              </View>
              <View
                width={"92%"}
                //UNSAFE_style={{ overflow: "auto" }}
                UNSAFE_className={styles.label_wrapper}
              >
                <ContactsContactMultipleTag
                  createParams={createParams}
                  fieldName={"cc"}
                  specificType="email"
                  setContactsSelected={newEmailFields.cc}
                />
              </View>
            </View>
          )}
        </View>
      </Flex>
    );
  }

  function renderDesktopNonEmailAdvancedFields(): React.ReactElement {
    if (!showCcNotify) {
      return (
        <Flex
          direction={"row"}
          top={"5px"}
          alignSelf={"stretch"}
          position={"relative"}
          flexShrink={0}
          gap={"size-200"}
        >
          <View
            position={"relative"}
            width={"5%"}
            left={"8px"}
            top={"5px"}
          />
          <View width={"92%"} marginStart={"17px"}>
            <View width={"100%"} UNSAFE_className={styles.label_wrapper}>
              <button
                type="button"
                className={styles.advanced_fields_trigger}
                onClick={() => setShowCcNotify(true)}
              >
                {fromEmailAddress.length > 2
                  ? t("notify_from", {
                      ns: "layout_components",
                      defaultValue: "Notify, from:",
                    })
                  : t("notify_only", {
                      ns: "layout_components",
                      defaultValue: "Notify:",
                    })}
              </button>
            </View>
          </View>
        </Flex>
      );
    }

    return (
      <>
        <Flex
          direction={"row"}
          top={"5px"}
          alignSelf={"stretch"}
          position={"relative"}
          flexShrink={0}
          gap={"size-200"}
        >
          <View
            position={"relative"}
            width={"5%"}
            left={"8px"}
            top={"5px"}
          >
            {t("notify", { ns: "layout_components" })}:
          </View>
          <View
            width={"92%"}
            marginStart={"17px"}
            //UNSAFE_style={{ overflow: "auto" }}
            UNSAFE_className={styles.label_wrapper}
          >
            <ContactsFilterMultipleTag
              createParams={createParams}
              setContactsSelected={newEmailFields.notify}
              fieldName={"notify"}
              params={{
                onlyIds: false,
                showOnlyUc: false,
                onlyUser: 1,
                conjuctionDistrGroups: 0,
                ORDER: "",
              }}
            />
          </View>
        </Flex>
        {fromEmailAddress.length > 2 && (
          <Flex
            direction={"row"}
            top={"5px"}
            alignSelf={"stretch"}
            position={"relative"}
            flexShrink={0}
            gap={"size-200"}
          >
            <View
              position={"relative"}
              width={"5%"}
              left={"8px"}
              top={"5px"}
            >
              {t("from", { ns: "layout_components" })}:
            </View>
            <View
              width={"92%"}
              marginStart={"17px"}
              position={"relative"}
              UNSAFE_className={styles.label_wrapper}
            >
              <DropDownList
                value={newEmailFields.fromValue}
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  zIndex: 0,
                  border: "none",
                  marginLeft: "0",
                }}
                data={fromEmailAddress}
                textField="name"
                dataItemKey="id"
                onChange={(event: DropDownListChangeEvent) => {
                  handleMailboxChange(event);
                }}
              />
            </View>
          </Flex>
        )}
      </>
    );
  }

  const desktopEditorOffset =
      String(newEmailFields.messageType) === "7"
        ? showCcNotify
          ? 2
          : 2
        : showCcNotify
          ? String(newEmailFields.messageType) === "12"
            ? 2
            : 2
          : 2;

  const desktopEditorHeight = (() => {
      const messageType = String(newEmailFields.messageType);
      let editorHeight = 330;

    if (messageType === "7") {
      editorHeight = showCcNotify ? 290 : 360;
    } else if (messageType === "1") {
      editorHeight = showCcNotify ? 280 : 320;
    } else if (messageType === "12") {
      editorHeight = showCcNotify ? 300 : 340;
    } else if (messageType === "6") {
      editorHeight = showCcNotify ? 260 : 300;
    } else {
      editorHeight = showCcNotify ? 290 : 330;
    }

    if (fileList.length > 0) {
      editorHeight = Math.max(220, editorHeight - 36);
    }

    return editorHeight;
  })();

  const desktopWindowHeight = (() => {
    const messageType = String(newEmailFields.messageType);
    let baseHeight = 740;

    if (messageType === "7") {
      baseHeight = showCcNotify ? 820 : 740;
    } else if (messageType === "12") {
      baseHeight = showCcNotify ? 800 : 760;
    } else if (messageType === "6") {
      baseHeight = showCcNotify ? 840 : 780;
    } else if (messageType === "1") {
      baseHeight = showCcNotify ? 760 : 720;
    } else {
      baseHeight = showCcNotify ? 780 : 740;
    }

    if (fileList.length > 0) {
      baseHeight += 56;
    }

    return Math.min(window.innerHeight - 32, baseHeight);
  })();

  function newMessageDialogheader(): React.ReactElement {
    if (isCompactSheetLayout) {
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          UNSAFE_className={styles.mobile_sheet_header_row}
        >
          <Text UNSAFE_className={styles.mobile_sheet_title}>
            {t("new_message", { ns: "layout_components" })}
          </Text>
          <button
            type="button"
            className={styles.mobile_sheet_cancel}
            onClick={() => closeDialog(false)}
          >
            {t("Cancel", { ns: "layout_components", defaultValue: "Cancel" })}
          </button>
        </Flex>
      );
    }
    if (isMobile) {
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"start"}
        >
          <Flex
            direction={"row"}
            UNSAFE_className={styles.message_heading}
            gap={"10px"}
          >
            {t("new_message", { ns: "layout_components" })}{" "}
            <NewMessageIcon size={18} />
          </Flex>
        </Flex>
      );
    } else
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          margin={"10px"}
        >
          <Flex
            direction={"row"}
            UNSAFE_className={styles.message_heading}
            gap={"10px"}
          >
            {t("new_message", { ns: "layout_components" })}{" "}
            <NewMessageIcon size={18} />
          </Flex>
          <Flex
            direction={"row"}
            UNSAFE_className={styles.label_message_type}
            gap={"10px"}
            alignContent={"center"}
            alignItems={"center"}
          >
            {t("message_type", { ns: "layout_components" })}
            <DropDownList
              value={newEmailFields.messageTypeValue}
              style={{ width: "200px", backgroundColor: "white" }}
              data={messageTypeOptions}
              textField="text"
              dataItemKey="id"
              onChange={(event: DropDownListChangeEvent) => {
                setShowCcNotify(false);
                setNewEmailFields({
                  ...newEmailFields,
                  messageType: event.value.type,
                  messageTypeValue: event.value,
                  cc: [],
                  to: [],
                });
              }}
            />
          </Flex>
        </Flex>
      );
  }

  function minimizedDialogheader(): React.ReactElement {
    return (
      <Flex
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Flex
          direction={"row"}
          UNSAFE_className={styles.message_heading_small}
          gap={"10px"}
        >
          {newEmailFields.subject
            ? truncateText(newEmailFields.subject, 15)
            : t("new_message", { ns: "layout_components" })}{" "}
          <NewMessageIcon size={18} />
        </Flex>
      </Flex>
    );
  }

  function newMessageDialogContent(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={"center"} marginTop={300}>
            <Loader size="medium" type={"pulsing"} />
          </Flex>
        );
      } else
        return (
          <Flex
            direction={"column"}
            UNSAFE_className={styles.message_modal}
            UNSAFE_style={{
              height: isCompactSheetLayout ? "100%" : "calc(100dvh - 120px)",
            }}
          >
            <Flex
              direction={"column"}
              width={"100%"}
              height={"100%"}
              gap={"4px"}
              flex={1}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.message_options}
              UNSAFE_style={{
                display: "grid",
                gridTemplateRows: "auto auto auto auto auto auto auto 1fr", // Form fields + editor (1fr)
              }}
            >
              {/* Message Type Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "1" }}
                width={"calc(100vw - 32px)"}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"18%"}
                >
                  {t("type", {
                    ns: "layout_components",
                    defaultValue: "Type",
                  })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"82%"}
                >
                  <DropDownList
                    value={newEmailFields.messageTypeValue}
                    style={{ width: "100%", backgroundColor: "white" }}
                    data={messageTypeOptions}
                    textField="text"
                    dataItemKey="id"
                    onChange={(event: DropDownListChangeEvent) => {
                      setShowCcNotify(false);
                      setNewEmailFields({
                        ...newEmailFields,
                        messageType: event.value.type,
                        messageTypeValue: event.value,
                        cc: [],
                        to: [],
                      });
                    }}
                  />
                </View>
              </Flex>

              {/* Context Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "2", overflow: "visible", zIndex: 2 }}
                top={'0px'}
              >
                <View
                  UNSAFE_className={`${styles.label_wrapper_mobile} ${styles.context_selection_host_mobile}`}
                  width={"18%"}
                >
                  {t("context", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={`${styles.label_wrapper_mobile} ${styles.context_selection_host_mobile}`}
                  width={"82%"}
                >
                  <ContextSelection
                    key={`ctx-${String(composeFolderContextId ?? selectedItemId ?? "none")}`}
                    value={displayContextSelection}
                    onSelectionChange={setMessageContextSelection}
                    folderStatusMap={folderStatusMapping}
                    projectStatusMap={projectStatusMapping}
                    activityStatusMap={activityStatusMapping}
                    isReadOnly={false}
                    showBorders={false}
                    allowClearSelection={true}
                    allowAdd={false}
                    explicitContextId={contextSelectionDefaultValue}
                    onExplicitContextResolveError={onContextPathResolveError}
                    aria-label={t("context", { ns: "layout_components" })}
                  />
                </View>
              </Flex>

              {/* Subject Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "3" }}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"18%"}
                >
                  {t("subject", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"82%"}
                >
                  <TextField
                    id="txtSubject"
                    ref={txtSubjectRef}
                    defaultValue={newEmailFields.subject}
                    value={newEmailFields.subject}
                    width={"100%"}
                    onChange={(e) =>
                      setNewEmailFields({ ...newEmailFields, subject: e })
                    }
                    UNSAFE_style={{
                      backgroundColor: "transparent !important",
                      padding: "5px",
                      color: "black",
                    }}
                  />
                </View>
              </Flex>

              {/* Conditional Fields Container */}
              <Flex direction={"column"} UNSAFE_style={{ gridRow: "4" }}>
                {String(newEmailFields.messageType) === "7" && (
                  <>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"36px"}
                      marginBottom={"4px"}
                    >
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"18%"}
                      >
                        {t("to", { ns: "layout_components" })} :
                      </View>
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"82%"}
                      >
                        <ContactsContactMultipleTag
                          createParams={createParams}
                          fieldName={"to"}
                          specificType="email"
                          setContactsSelected={newEmailFields.to}
                        />
                      </View>
                    </Flex>

                    {/* CC and Notify Toggle Label */}
                    {!showCcNotify && (
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        position={"relative"}
                      >
                        <div
                          onClick={() => setShowCcNotify(true)}
                          style={{ width: "100%" }}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"100%"}
                          >
                            {t("cc", { ns: "layout_components" })},{" "}
                            {t("notify", { ns: "layout_components" })}
                          </View>
                        </div>
                      </Flex>
                    )}

                    {/* CC and Notify Fields (Collapsible) */}
                    {showCcNotify && (
                      <>
                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"36px"}
                          marginBottom={"4px"}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"18%"}
                          >
                            {t("cc", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"82%"}
                          >
                            <ContactsContactMultipleTag
                              createParams={createParams}
                              fieldName={"cc"}
                              specificType="email"
                              setContactsSelected={newEmailFields.cc}
                            />
                          </View>
                        </Flex>

                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"36px"}
                          marginBottom={"4px"}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"18%"}
                          >
                            {t("notify", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"82%"}
                          >
                            <View marginTop={"5px"}>
                              <ContactsFilterMultipleTag
                                createParams={createParams}
                                setContactsSelected={newEmailFields.notify}
                                fieldName={"notify"}
                                params={{
                                  onlyIds: false,
                                  showOnlyUc: false,
                                  onlyUser: 1,
                                  conjuctionDistrGroups: 0,
                                  ORDER: "",
                                }}
                              />
                            </View>
                          </View>
                        </Flex>
                      </>
                    )}
                  </>
                )}

                {String(newEmailFields.messageType) === "12" && (
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    position={"relative"}
                    height={"36px"}
                    marginBottom={"4px"}
                  >
                    <View
                      UNSAFE_className={styles.label_wrapper_mobile}
                      width={"18%"}
                    >
                      {t("meeting", { ns: "layout_components" })} :
                    </View>
                    <View
                      UNSAFE_className={styles.label_wrapper_mobile}
                      width={"82%"}
                    >
                      <ContactsFilterMultipleTag
                        createParams={createParams}
                        fieldName={"participants"}
                        setContactsSelected={newEmailFields.participants}
                        params={{
                          onlyIds: false,
                          showOnlyUc: false,
                          conjuctionDistrGroups: 0,
                          ORDER: "",
                        }}
                      />
                    </View>
                  </Flex>
                )}

                {String(newEmailFields.messageType) === "6" && (
                  <>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"36px"}
                      marginBottom={"4px"}
                    >
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"18%"}
                      >
                        {t("phone", { ns: "layout_components" })} :
                      </View>
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"82%"}
                      >
                        <ContactsContactMultipleTag
                          createParams={createParams}
                          fieldName={"to"}
                          specificType="phone"
                          setContactsSelected={newEmailFields.to}
                        />
                      </View>
                    </Flex>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                    >
                      <Flex width={"100%"}>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="131072"
                            checked={newEmailFields.phoneStatus === "131072"}
                            label={t("incoming", { ns: "layout_components" })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="262144"
                            checked={newEmailFields.phoneStatus === "262144"}
                            label={t("outgoing", { ns: "layout_components" })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="524288"
                            checked={newEmailFields.phoneStatus === "524288"}
                            label={t("unsuccessful", {
                              ns: "layout_components",
                            })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>

              {String(newEmailFields.messageType) !== "7" && (
                <>
                  {!showCcNotify ? (
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"36px"}
                      marginBottom={"4px"}
                      UNSAFE_style={{ gridRow: "5" }}
                    >
                      <div
                        onClick={() => setShowCcNotify(true)}
                        style={{ width: "100%" }}
                      >
                        <View
                          UNSAFE_className={styles.label_wrapper_mobile}
                          width={"100%"}
                        >
                          {fromEmailAddress.length > 2
                            ? t("notify_from", {
                                ns: "layout_components",
                                defaultValue: "Notify, from:",
                              })
                            : t("notify_only", {
                                ns: "layout_components",
                                defaultValue: "Notify:",
                              })}
                        </View>
                      </div>
                    </Flex>
                  ) : (
                    <>
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        position={"relative"}
                        height={"36px"}
                        marginBottom={"4px"}
                        UNSAFE_style={{ gridRow: "5" }}
                      >
                        <View
                          UNSAFE_className={styles.label_wrapper_mobile}
                          width={"18%"}
                        >
                          {t("notify", { ns: "layout_components" })} :
                        </View>
                        <View
                          UNSAFE_className={styles.label_wrapper_mobile}
                          width={"82%"}
                        >
                          <View marginTop={"5px"}>
                            <ContactsFilterMultipleTag
                              createParams={createParams}
                              setContactsSelected={newEmailFields.notify}
                              fieldName={"notify"}
                              params={{
                                onlyIds: false,
                                showOnlyUc: false,
                                onlyUser: 1,
                                conjuctionDistrGroups: 0,
                                ORDER: "",
                              }}
                            />
                          </View>
                        </View>
                      </Flex>
                      {fromEmailAddress.length > 2 && (
                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"36px"}
                          marginBottom={"4px"}
                          UNSAFE_style={{ gridRow: "6" }}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"18%"}
                          >
                            {t("from", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"82%"}
                          >
                            <View marginTop={"5px"}>
                              <DropDownList
                                value={newEmailFields.fromValue}
                                style={{
                                  width: "100%",
                                  backgroundColor: "white",
                                  zIndex: 0,
                                  border: "none",
                                  marginLeft: "0",
                                }}
                                data={fromEmailAddress}
                                textField="name"
                                dataItemKey="id"
                                onChange={(event: DropDownListChangeEvent) => {
                                  handleMailboxChange(event);
                                }}
                              />
                            </View>
                          </View>
                        </Flex>
                      )}
                    </>
                  )}
                </>
              )}

              {/* File List (Conditional) */}
              {fileList.length > 0 && (
                <Flex
                  direction={"column"}
                  position={"relative"}
                  width={"100%"}
                  UNSAFE_style={{ gridRow: "7" }}
                >
                  <Flex
                    wrap
                    direction="row"
                    gap="size-100"
                    alignItems="center"
                    width="100%"
                    UNSAFE_className={styles.attachment_list_container}
                    UNSAFE_style={{
                      backgroundColor: "#f5f7f8",
                      borderRadius: "8px",
                      padding: "8px",
                      zIndex: 0,
                    }}
                  >
                    {fileList.map((item: any, index: number) => (
                      <View
                        key={item.id}
                        width={"30%"}
                        position={"relative"}
                        height={"20px"}
                        UNSAFE_className={styles.icon_cancel_parent}
                        id={`${item.id}_${index}`}
                      >
                        <View
                          position={"relative"}
                          width={"24px"}
                          height={"24px"}
                          UNSAFE_className={styles.icon_cancel}
                        >
                          <div onClick={() => deleteFile(item.id)}>
                            <CancelIcon size={18} />
                          </div>
                        </View>
                        <View
                          position={"relative"}
                          left={"5px"}
                          width={"70%"}
                          UNSAFE_className={styles.label_attachment}
                        >
                          {truncateText(item.fileName, 10)}
                        </View>
                        <View
                          position={"absolute"}
                          left={"8px"}
                          width={"28px"}
                          height={"28px"}
                          UNSAFE_className={styles.attachment_icon}
                        >
                          <AttachmentIcon size={18} />
                        </View>
                      </View>
                    ))}
                  </Flex>
                </Flex>
              )}

              {/* TinyMCE Editor - Takes remaining space */}
              <Flex
                direction={"column"}
                position={"relative"}
                width={"100%"}
                height={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                zIndex={0}
                flex={1}
                minHeight={"220px"}
                gridRow={"8"}
                marginTop={"8px"}
                marginX={"0"}
              >
                <TinyMCEEditor
                  key={"tiny_editor_mobile"}
                  initialValue={newEmailFields.emailContent}
                  onChange={(e: any) => {
                    setNewEmailFields({ ...newEmailFields, emailContent: e });
                  }}
                  onSubmitShortcut={() => {
                    if (!showLoader) {
                      handleSendEmail();
                    }
                  }}
                  onEscapeShortcut={() => closeDialog(false)}
                  onCloseShortcut={() => closeDialog(false)}
                  autoFocus={false}
                  height={"100%"}
                />
              </Flex>
            </Flex>
          </Flex>
        );
    }
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={"center"} marginTop={300}>
          <Loader size="medium" type={"pulsing"} />
        </Flex>
      );
    } else {
      return (
        <div
          {...getRootProps()}
          className={isDragActive ? styles.drag_active : ""}
          style={{
            height: "auto",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <input {...getInputProps()} />

          {/* Overlay that appears only when dragging */}
          {isDragActive && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
                backgroundColor: "rgba(38, 128, 235, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Text UNSAFE_style={{ fontWeight: "bold", fontSize: "18px" }}>
                {t("drop_files_here", { ns: "layout_components" })}
              </Text>
            </div>
          )}
          <View
            UNSAFE_className={styles.message_modal}
            UNSAFE_style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <View
              width={"100%"}
              flexGrow={1}
              flexShrink={1}
              UNSAFE_style={{
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Flex
                direction={"column"}
                width={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                UNSAFE_className={styles.message_options}
                UNSAFE_style={{ minHeight: 0 }}
              >
              <Flex
                direction={"row"}
                top={"2px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
                UNSAFE_style={{ overflow: "visible", zIndex: 2 }}
              >
                <View position={"relative"} width={"5%"} left={"8px"} 
                top={"5px"}
                >
                  {t("context", { ns: "layout_components" })}:
                </View>
                <View
                  width={"92%"}
                  marginStart={"17px"}
                  UNSAFE_className={`${styles.label_wrapper} ${styles.context_selection_host}`}
                >
                  <ContextSelection
                    key={`ctx-${String(composeFolderContextId ?? selectedItemId ?? "none")}`}
                    value={displayContextSelection}
                    onSelectionChange={setMessageContextSelection}
                    folderStatusMap={folderStatusMapping}
                    projectStatusMap={projectStatusMapping}
                    activityStatusMap={activityStatusMapping}
                    isReadOnly={false}
                    showBorders={false}
                    allowClearSelection={true}
                    allowAdd={false}
                    allowTransparentBG={true}
                    explicitContextId={contextSelectionDefaultValue}
                    onExplicitContextResolveError={onContextPathResolveError}
                    aria-label={t("context", { ns: "layout_components" })}
                  />
                </View>
              </Flex>
              <Flex
                direction={"row"}
                top={"2px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
              >
                <View
                  position={"relative"}
                  width={"5%"}
                  left={"8px"}
                  alignSelf={"center"}
                >
                  {t("subject", { ns: "layout_components" })}
                </View>
                <View
                  width={"92%"}
                  marginStart={"17px"}
                  UNSAFE_className={styles.label_wrapper}
                >
                  <TextField
                    id="txtSubject"
                    ref={txtSubjectRef}
                    defaultValue={newEmailFields.subject}
                    value={newEmailFields.subject}
                    width={"100%"}
                    height={"25px"}
                    onChange={(e) =>
                      setNewEmailFields({ ...newEmailFields, subject: e })
                    }
                    UNSAFE_style={{
                      border: "none !important",
                      boxShadow: "none",
                      backgroundColor: "transparent !important",
                      padding: "5px 0px",
                      color: "black",
                    }}
                    UNSAFE_className="borderless_textfield"
                  />
                </View>
              </Flex>
              {String(newEmailFields.messageType) === "7" &&
                renderDesktopEmailPrimaryRow()}
              {String(newEmailFields.messageType) === "7" &&
                showCcNotify &&
                renderDesktopEmailOptionalFields()}
                {String(newEmailFields.messageType) === "12" && (
                  <Flex
                    direction={"row"}
                    top={"5px"}
                    alignSelf={"stretch"}
                    position={"relative"}
                    flexShrink={0}
                    gap={"size-200"}
                  >
                    <View
                      position={"relative"}
                      width={"5%"}
                      left={"8px"}
                      top={"5px"}
                    >
                      {t("participants", {
                        ns: "layout_components",
                        defaultValue: "Participants",
                      })}
                      :
                    </View>
                    <View
                      width={"92%"}
                      marginStart={"17px"}
                      //UNSAFE_style={{ overflow: "auto" }}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <ContactsFilterMultipleTag
                        createParams={createParams}
                        fieldName={"participants"}
                        setContactsSelected={newEmailFields.participants}
                        params={{
                          onlyIds: false,
                          //organizationId: 130946101,
                          showOnlyUc: false,
                          conjuctionDistrGroups: 0,
                          ORDER: "",
                        }}
                      />
                    </View>
                  </Flex>
                )}

              {String(newEmailFields.messageType) === "6" && (
                <Flex
                  direction={"row"}
                  top={"5px"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  flexShrink={0}
                  gap={"size-200"}
                >
                  <View
                    position={"relative"}
                    width={"5%"}
                    left={"8px"}
                    top={"5px"}
                  >
                    {t("phone", { ns: "layout_components" })}:
                  </View>
                  <View
                    width={"92%"}
                    marginStart={"17px"}
                    UNSAFE_style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "16px",
                      minWidth: 0,
                    }}
                  >
                    <View
                      width={"42%"}
                      //UNSAFE_style={{ overflow: "auto" }}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <ContactsContactMultipleTag
                        createParams={createParams}
                        fieldName={"to"}
                        specificType="phone"
                        setContactsSelected={newEmailFields.to}
                      />
                    </View>

                    <Flex
                      direction={"row"}
                      gap={"20px"}
                      width={"58%"}
                      alignItems={"center"}
                      //UNSAFE_style={{ overflow: "auto" }}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <RadioButton
                        name="phoneStatus"
                        value="131072"
                        checked={newEmailFields.phoneStatus === "131072"}
                        label={t("incoming", { ns: "layout_components" })}
                        onChange={(event) => {
                          setNewEmailFields({
                            ...newEmailFields,
                            phoneStatus: event.value,
                          });
                        }}
                      />

                      <RadioButton
                        name="phoneStatus"
                        value="262144"
                        checked={newEmailFields.phoneStatus === "262144"}
                        label={t("outgoing", { ns: "layout_components" })}
                        onChange={(event) => {
                          setNewEmailFields({
                            ...newEmailFields,
                            phoneStatus: event.value,
                          });
                        }}
                      />

                      <RadioButton
                        name="phoneStatus"
                        value="524288"
                        checked={newEmailFields.phoneStatus === "524288"}
                        label={t("unsuccessful", { ns: "layout_components" })}
                        onChange={(event) => {
                          setNewEmailFields({
                            ...newEmailFields,
                            phoneStatus: event.value,
                          });
                        }}
                      />
                    </Flex>
                  </View>
                </Flex>
              )}
              {String(newEmailFields.messageType) !== "7" &&
                renderDesktopNonEmailAdvancedFields()}
            </Flex>
            <Flex
              direction={"column"}
              position={"relative"}
              marginTop={`${desktopEditorOffset}px`}
            >
              {fileList.length > 0 && (
                <Flex
                  wrap
                  direction="row"
                  gap="size-100"
                  alignItems="center"
                  width="98%"
                  marginStart="11px"
                  marginTop={'10px'}
                  UNSAFE_className={styles.attachment_list_container}
                  UNSAFE_style={{
                    backgroundColor: "#f5f7f8",
                    padding: "10px",
                    borderRadius: "8px",
                    zIndex: 0,
                    maxHeight: "168px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {fileList.map((item: any, index: number) => (
                    <View
                      key={item.id}
                      width={"48%"}
                      position={"relative"}
                      height={"40px"}
                      UNSAFE_className={styles.icon_cancel_parent}
                      id={`${item.id}_${index}`}
                    >
                      <View
                        position={"relative"}
                        top={"5px"}
                        width={"24px"}
                        height={"24px"}
                        UNSAFE_className={styles.icon_cancel}
                      >
                        <div onClick={() => deleteFile(item.id)}>
                          <CancelIcon size={18} />
                        </div>
                      </View>
                      <View
                        position={"relative"}
                        top={"10px"}
                        left={"35px"}
                        width={"70%"}
                        UNSAFE_className={styles.label_attachment}
                      >
                        {truncateText(item.fileName, 20)}
                      </View>
                      <View
                        position={"absolute"}
                        top={"6px"}
                        left={"8px"}
                        width={"28px"}
                        height={"28px"}
                        UNSAFE_className={styles.attachment_icon}
                      >
                        <AttachmentIcon size={18} />
                      </View>
                    </View>
                  ))}
                </Flex>
              )}
            </Flex>

              <Flex
                direction={"column"}
                position={"relative"}
                marginTop={"4px"}
                marginBottom={"8px"}
                width={"calc(100% - 16px)"}
                height={"auto"}
              alignItems={"start"}
              justifyContent={"start"}
              zIndex={0}
              flexShrink={0}
              marginX={"8px"}
              UNSAFE_style={{ overflow: "hidden" }}
            >
              <TinyMCEEditor
                initialValue={newEmailFields.emailContent}
                onChange={(e: any) => {
                  setNewEmailFields({ ...newEmailFields, emailContent: e });
                }}
                onSubmitShortcut={() => {
                  if (!showLoader) {
                    handleSendEmail();
                  }
                }}
                onEscapeShortcut={() => closeDialog(false)}
                onCloseShortcut={() => closeDialog(false)}
                autoFocus={false}
                height={desktopEditorHeight}
              />
            </Flex>
            </View>
          </View>
        </div>
      );
    }
  }

  function newMessageDialogFooter(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={"center"}>
            <Loader size="medium" type={"pulsing"} />
          </Flex>
        );
      } else
        return (
          <Flex
            gap={"10px"}
            direction={"column"}
            alignItems={"stretch"}
            UNSAFE_className={styles.email_action_bar}
          >
            <Flex
              gap={"8px"}
              alignItems={"center"}
              UNSAFE_style={{ display: "none" }}
            >
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "10px" }}>
                  <TemplateIcon size={18} />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("use_template", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "10px", color: "#1976d2" }}>
                  <i className="bi bi-gear" />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("advanced", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "10px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                  <Text UNSAFE_className={styles.btn_attachment}>
                    {t("add_attachment", { ns: "layout_components" })}
                  </Text>
                </ActionButton>
              </FileTrigger>
            </Flex>
            <Flex
              gap={"8px"}
              alignItems={"center"}
              justifyContent={"end"}
              margin={"5px"}
              width={"100%"}
            >
              {/* <ActionButton onPress={e => saveDraft()} UNSAFE_className={`${styles.action_button} ${styles.draft_button}`}>
                <Text UNSAFE_className={styles.btn_attachment}>{t('save_draft_and_close', { ns: 'layout_components' })}</Text>
              </ActionButton> */}
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "10px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                </ActionButton>
              </FileTrigger>
              <ContextMenuComponent
                target=".context-target"
                id="contextmenu"
                ref={contextMenuRef}
                items={menuItems}
                select={handleMenuItemClick}
              />
              <span
                className={`${styles.action_button} ${styles.draft_button} context-target`}
                style={{ width: "50px", justifyContent: "center" }}
                role="button"
                tabIndex={0}
              >
                <MoreIcon size={18} />
              </span>

              <ActionButton
                width={"250px"}
                onPress={() => handleSendEmail()}
                UNSAFE_className={`${styles.action_button_email} ${styles.send_button}`}
              >
                <Text UNSAFE_className={styles.btn_send}>
                  {t("send", { ns: "layout_components" })}
                </Text>
              </ActionButton>
            </Flex>
          </Flex>
        );
    }
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={"center"}>
          <Loader size="medium" type={"pulsing"} />
        </Flex>
      );
    } else
      return (
        <WindowActionsBar layout={"stretched"}>
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"10px"}
            width={"100%"}
            UNSAFE_className={styles.email_action_bar}
          >
            <Flex gap={"8px"} alignItems={"center"}>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "25px" }}>
                  <TemplateIcon size={18} />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("use_template", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "25px", color: "#1976d2" }}>
                  <i className="bi bi-gear" />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("advanced", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "25px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                  <Text UNSAFE_className={styles.btn_attachment}>
                    {t("add_attachment", { ns: "layout_components" })}
                  </Text>
                </ActionButton>
              </FileTrigger>
            </Flex>
            <Flex gap={"8px"} alignItems={"center"}>
              <ActionButton
                onPress={() => handleSaveDraft()}
                UNSAFE_className={`${styles.action_button} ${styles.draft_button}`}
              >
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("save_draft_and_close", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton
                onPress={() => handleSendEmail()}
                UNSAFE_className={`${styles.action_button} ${styles.send_button}`}
              >
                <Text UNSAFE_className={styles.btn_send}>
                  {t("send", { ns: "layout_components" })}
                </Text>
              </ActionButton>
            </Flex>
          </Flex>
        </WindowActionsBar>
      );
  }

  return (
    <div onKeyUp={handleClose}>
      {isCompactSheetLayout ? (
        <ModalOverlay
          isOpen={isOpen}
          isDismissable
          className={styles.mobile_sheet_overlay}
          onOpenChange={(open) => {
            if (!open) {
              closeDialog(false);
            }
          }}
        >
          <Modal className={styles.mobile_sheet_modal}>
            <Dialog className={styles.mobile_sheet_dialog}>
              <div className={styles.mobile_sheet_handle} />
              <div className={styles.mobile_sheet_header}>
                {newMessageDialogheader()}
              </div>
              <div className={styles.mobile_sheet_content}>
                {newMessageDialogContent()}
              </div>
              <div className={styles.mobile_sheet_footer}>
                {newMessageDialogFooter()}
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      ) : (
        <WindowComponent
          id={`new-message-window_${windowIndex}`}
          windowIndex={windowIndex}
          visible={isOpen}
          title={newMessageDialogheader()}
          minimizedTitle={minimizedDialogheader()}
          content={newMessageDialogContent()}
            footer={!isMobile ? newMessageDialogFooter() : undefined}
            className={styles.compose_window_shell}
            width={isMobile ? window.innerWidth : Math.min(window.innerWidth - 32, 920)}
            height={isMobile ? window.innerHeight : desktopWindowHeight}
            draggable={!isMobile}
            resizable={!isMobile}
            stickFooterToBottom={false}
          zIndex={1000}
          onClose={() => closeDialog(false)}
        />
      )}
    </div>
  );
}

export default NewMessageDialog;
